import { useState, useEffect } from "react";
import { 
  Settings as SettingsIcon, 
  Key, 
  Mail,
  Save,
  AlertCircle,
  CheckCircle,
  ExternalLink
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { getSettings, updateSettings } from "../lib/api";

export default function Settings() {
  const [settings, setSettings] = useState({
    moz_api_key: "",
    hunter_api_key: "",
    resend_api_key: "",
    sender_email: "",
    sender_name: "",
    mailing_address: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getSettings();
      setSettings(s => ({
        ...s,
        ...response.data,
        moz_api_key: response.data.moz_api_key || "",
        hunter_api_key: response.data.hunter_api_key || "",
        resend_api_key: response.data.resend_api_key || "",
        sender_email: response.data.sender_email || "",
        sender_name: response.data.sender_name || "",
        mailing_address: response.data.mailing_address || ""
      }));
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateSettings({
        id: "user_settings",
        ...settings
      });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const ApiKeyCard = ({ title, description, field, placeholder, link, linkText }) => (
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Key className="w-5 h-5 text-primary" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor={field}>API Key</Label>
          <Input
            id={field}
            type="password"
            placeholder={placeholder}
            value={settings[field]}
            onChange={(e) => setSettings(s => ({ ...s, [field]: e.target.value }))}
            data-testid={`${field}-input`}
          />
        </div>
        {link && (
          <a 
            href={link} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            {linkText}
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <div className="flex items-center gap-2 text-sm">
          {settings[field] ? (
            <>
              <CheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-green-500">Configured</span>
            </>
          ) : (
            <>
              <AlertCircle className="w-4 h-4 text-yellow-500" />
              <span className="text-yellow-500">Not configured (using free tier)</span>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-loading">
        <div className="skeleton h-8 w-32 rounded" />
        <div className="skeleton h-48 rounded-md" />
        <div className="skeleton h-48 rounded-md" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure API keys and preferences
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Free Tier Available</p>
            <p className="text-sm text-muted-foreground mt-1">
              This tool works without any API keys using free methods (estimated metrics, email scraping). 
              Add your own API keys below for more accurate data and higher volume.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email Settings */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-primary" />
            Email Settings
          </CardTitle>
          <CardDescription>
            Configure how outreach emails are sent
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sender_name">Sender Name</Label>
              <Input
                id="sender_name"
                placeholder="Your Name"
                value={settings.sender_name}
                onChange={(e) => setSettings(s => ({ ...s, sender_name: e.target.value }))}
                data-testid="sender-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="sender_email">Sender Email</Label>
              <Input
                id="sender_email"
                type="email"
                placeholder="you@example.com"
                value={settings.sender_email}
                onChange={(e) => setSettings(s => ({ ...s, sender_email: e.target.value }))}
                data-testid="sender-email-input"
              />
              <p className="text-xs text-muted-foreground">
                Every outreach email and follow-up is sent from this address. Its domain must be verified in Resend.
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="mailing_address">Mailing Address (email footer)</Label>
            <Input
              id="mailing_address"
              placeholder="Company name, street, city, country"
              value={settings.mailing_address}
              onChange={(e) => setSettings(s => ({ ...s, mailing_address: e.target.value }))}
              data-testid="mailing-address-input"
            />
            <p className="text-xs text-muted-foreground">
              Shown under the unsubscribe link in every email. US anti-spam law (CAN-SPAM) requires a valid postal address in commercial email.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* API Keys Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ApiKeyCard
          title="Resend (Email Sending)"
          description="Required for sending actual outreach emails. Free tier: 3,000 emails/month."
          field="resend_api_key"
          placeholder="re_..."
          link="https://resend.com/api-keys"
          linkText="Get API key from Resend"
        />
        
      </div>

      {/* Usage Info */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg">Current Limits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-muted-foreground">Email Sending</p>
              <p className="text-2xl font-bold mt-1">
                {settings.resend_api_key ? "3,000/mo" : "0/mo"}
              </p>
              <p className="text-xs text-muted-foreground">
                {settings.resend_api_key ? "Resend Free Tier" : "Add Resend key to send emails"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Domain Metrics</p>
              <p className="text-2xl font-bold mt-1">
                DataForSEO
              </p>
              <p className="text-xs text-muted-foreground">
                Configured on the server. Sites without data show no score.
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email Discovery</p>
              <p className="text-2xl font-bold mt-1">
                Site pages
              </p>
              <p className="text-xs text-muted-foreground">
                Only addresses published on the site are used. Nothing is guessed.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
